// The moon.
//
// It is scenery: painted into the background, and a banana passes in front of it.
// It moves, because a moon nailed to one spot for a whole match reads as a sticker
// on the sky, and it is not always there at all, because a sky that always looks
// the same is not a sky.
//
// Two rules hold it together.
//
// Where it sits, how big it is, and whether it exists, are drawn from the same
// per-round dice as the skyline — so a shared match code rebuilds the same moon.
// The draw happens last in makeCity, after every other consumer of the generator,
// so codes written before the moon moved still rebuild their city exactly.
//
// Its travel is *derived* from the clock, never accumulated frame by frame, and it
// is measured from the moment the round began rather than from page load. So
// everything that asks where the moon is gets one answer — there is no second copy
// to drift out of step — and every round opens with the moon exactly where the
// dice put it, however long the page had been sitting on the title screen.

import { W, H } from './canvas.js';
import { state } from './state.js';
import { MOON } from './config.js';
import { matchRand } from './rng.js';
import { mods } from './modes.js';

/**
 * Craters, as fractions of the radius, so they scale with the moon and can be spun
 * around its centre. The old numbers were absolute canvas coordinates, which only
 * worked because the moon never went anywhere.
 */
const CRATERS = [
  [-0.33, -0.33, 0.18],
  [0.33, 0.16, 0.11],
  [-0.16, 0.47, 0.09],
];

/**
 * Picks this round's moon, or decides there is not one. Called at the end of
 * makeCity so it reads from the round's seed without shifting the sequence that
 * lays out the buildings.
 */
export function spawnMoon() {
  // One roll decides whether there is a moon at all. Moonless rounds are the
  // honest version of "a sky that varies": a moon squeezed into a corner of every
  // round would still read as permanent furniture.
  if (matchRand(1, 100) <= Math.round(MOON.absentChance * 100)) {
    state.moon = null;
    return;
  }

  state.moon = {
    r: matchRand(MOON.minR, MOON.maxR),
    x: matchRand(Math.round(W * MOON.bandX[0]), Math.round(W * MOON.bandX[1])),
    y: matchRand(Math.round(H * MOON.bandY[0]), Math.round(H * MOON.bandY[1])),
    // Which way it travels, and where its face started, both from the dice, so
    // two moons from two rounds do not spin in lockstep.
    dir: matchRand(0, 1) ? 1 : -1,
    spin: matchRand(0, 359) * (Math.PI / 180),
    bornAt: state.clock,
  };
}

/**
 * Where the moon is right now, or null if this round has none — or if the mode has
 * no sky at all, which is how CLASSIC stays the original's sky: stars and nothing
 * else. Everything that cares about the moon comes through here, so the painting
 * and the lucky-shot check can never disagree about where it is.
 */
export function moonAt() {
  const m = state.moon;
  if (!m || !mods().sky) return null;

  const seconds = (state.clock - m.bornAt) / 1000;

  // It crosses the sky slowly, and wraps around the far edge rather than setting
  // for good: a moon that had gone would take the lucky-shot achievement, and the
  // LUNAR special and the viking helmet behind it, out of the rest of the match.
  // Rounds that open without a moon already cover "sometimes there is not one".
  const span = W + m.r * 2;
  const travelled = (((m.x + m.dir * MOON.driftPxPerSec * seconds + m.r) % span) + span) % span;

  return {
    x: travelled - m.r,
    y: m.y,
    r: m.r,
    rot: m.spin + (seconds / MOON.spinSeconds) * Math.PI * 2,
  };
}

/** The craters spun onto the moon's face, in canvas coordinates. */
export function moonCraters(moon) {
  const c = Math.cos(moon.rot);
  const s = Math.sin(moon.rot);

  return CRATERS.map(([cx, cy, cr]) => ({
    x: moon.x + (cx * c - cy * s) * moon.r,
    y: moon.y + (cx * s + cy * c) * moon.r,
    r: cr * moon.r,
  }));
}
